import { useFlags } from "launchdarkly-react-client-sdk";

import { PATIENT_PRONOUNS_FEATURE } from "constants/feature-flags";

import {
  APPOINTMENT_EMAIL_BUILDER_MERGE_TAGS,
  APPOINTMENT_EMAIL_BUILDER_MERGE_TAGS_WITH_PRONOUNS,
  EXTENDED_EMAIL_BUILDER_MERGE_TAGS,
  EXTENDED_EMAIL_BUILDER_MERGE_TAGS_WITH_PRONOUNS,
  type EmailBuilderMergeTags
} from "./email-builder-merge-tags";

/**
 * The merge-tag set to register on any patient-scope Unlayer editor.
 *
 * Every surface that can `loadDesign` a stored Designer Email template needs the SAME set the
 * template was authored against, because Unlayer only tokenises a token into a chip when that token
 * is registered on that editor instance — an unregistered one renders as its raw `!--SUNO:tag--`
 * value. Authoring against `EXTENDED_*` in Settings → Templates while consuming surfaces registered
 * only the core seven is exactly the SN-19526 follow-up.
 *
 * Returns a module constant, never a freshly built object: `email-builder.component.tsx` re-runs
 * `setMergeTags` on every identity change, and `template-content.component.tsx` already memoizes for
 * the same reason ("a new identity every render risks re-initializing the builder mid-edit"). That is
 * also why this takes no parameters — a caller passing an inline object would defeat it silently.
 * A surface needing extra tags spreads this into its own `useMemo`, as Settings → Templates does for
 * the physician namespace.
 */
export const useEmailBuilderMergeTags = (): EmailBuilderMergeTags =>
  useFlags()[PATIENT_PRONOUNS_FEATURE]
    ? EXTENDED_EMAIL_BUILDER_MERGE_TAGS_WITH_PRONOUNS
    : EXTENDED_EMAIL_BUILDER_MERGE_TAGS;

/**
 * The set for editors whose templates render against an APPOINTMENT — Settings → Templates and
 * Settings → Appointment Types. Adds the telehealth tags, which resolve from
 * `appointment.zoom_meeting.join_url` and are therefore blank on every other surface.
 *
 * The registration rule in the hook above still binds: any surface that can `loadDesign` a template
 * authored here must register THIS set, or the telehealth tokens render as raw `!--SUNO:tag--` text
 * in the editor. Today those are the three editors that call this, plus the New Email form while it
 * is a notification preview (`previewMessageType`), which the backend renders against an appointment.
 * Keep it that way: anywhere else, the telehealth tags can only ever send blank.
 *
 * Returns a module constant for the same identity-stability reason as its sibling.
 */
export const useAppointmentEmailBuilderMergeTags = (): EmailBuilderMergeTags =>
  useFlags()[PATIENT_PRONOUNS_FEATURE]
    ? APPOINTMENT_EMAIL_BUILDER_MERGE_TAGS_WITH_PRONOUNS
    : APPOINTMENT_EMAIL_BUILDER_MERGE_TAGS;
